/**
 * Prediction Engine V2 — Monte Carlo Simulator
 *
 * Runs Monte Carlo simulations for both regular and sprint weekends.
 * Produces expected points, percentiles, and component breakdowns.
 */

import { DEFAULT_SEASON } from "../constants";
import {
  getQualifyingPoints,
  getRacePoints,
  getSprintPoints,
} from "../fantasy-points";
import { getScoringRules } from "../scoring-rules";
import {
  clamp,
  sampleBernoulli,
  sampleNormal,
  samplePoisson,
} from "./distributions";
import type {
  ComponentBreakdown,
  ConstructorSimParams,
  DriverSimParams,
  SimulationResult,
} from "./types";

// ============================================================================
// Single Simulation — Regular Weekend
// ============================================================================

export function simulateRegularWeekend(
  params: DriverSimParams,
  season = DEFAULT_SEASON,
): number {
  const rules = getScoringRules(season);

  // 1. Qualifying
  const qualiPos = clamp(
    Math.round(sampleNormal(params.expectedQuali, params.qualiStdDev)),
    1,
    20,
  );
  const qualiPoints = getQualifyingPoints(qualiPos);

  // 2. DNF check
  if (sampleBernoulli(params.dnfProb)) {
    const partialOvertakes = samplePoisson(params.expectedOvertakes * 0.3);
    return qualiPoints + rules.raceDnfPenalty + partialOvertakes;
  }

  // 3. Race position
  const raceDelta = sampleNormal(
    params.avgQualiToRaceDelta,
    params.raceDeltaStdDev,
  );
  const racePos = clamp(Math.round(qualiPos - raceDelta), 1, 20);
  const racePoints = getRacePoints(racePos);

  // 4. Positions gained
  const posGained = qualiPos - racePos;

  // 5. Overtakes
  const overtakes = samplePoisson(params.expectedOvertakes);

  // 6. Fastest lap
  const hasFastestLap = sampleBernoulli(params.fastestLapProb);

  // 7. Driver of the Day
  const hasDotd = sampleBernoulli(params.dotdProb);

  return (
    qualiPoints +
    racePoints +
    posGained +
    overtakes +
    (hasFastestLap ? rules.fastestLapRace : 0) +
    (hasDotd ? rules.driverOfTheDay : 0)
  );
}

// ============================================================================
// Single Simulation — Sprint Weekend
// ============================================================================

export function simulateSprintWeekend(
  params: DriverSimParams,
  season = DEFAULT_SEASON,
): number {
  const rules = getScoringRules(season);

  // === SPRINT PORTION ===

  // Sprint qualifying position
  const sprintQualiPos = clamp(
    Math.round(sampleNormal(params.expectedQuali, params.qualiStdDev)),
    1,
    20,
  );

  let sprintTotal = 0;
  let sprintRacePos = sprintQualiPos;

  if (sampleBernoulli(params.sprintDnfProb)) {
    // Sprint DNF
    const partialOvertakes = samplePoisson(
      params.expectedSprintOvertakes * 0.3,
    );
    sprintTotal = rules.sprintDnfPenalty + partialOvertakes;
  } else {
    // Sprint race
    const sprintDelta = sampleNormal(
      params.avgSprintQualiToRaceDelta,
      params.sprintDeltaStdDev,
    );
    sprintRacePos = clamp(Math.round(sprintQualiPos - sprintDelta), 1, 20);
    const sprintPosPoints = getSprintPoints(sprintRacePos);
    const sprintPosGained = sprintQualiPos - sprintRacePos;
    const sprintOvertakes = samplePoisson(params.expectedSprintOvertakes);
    const sprintFL = sampleBernoulli(params.sprintFastestLapProb);

    sprintTotal =
      sprintPosPoints +
      sprintPosGained +
      sprintOvertakes +
      (sprintFL ? rules.fastestLapSprint : 0);
  }

  // === MAIN RACE PORTION ===
  // Sprint result weakly adjusts GP quali prediction (separate sessions)
  const sprintAdjustedQuali = params.expectedQuali * 0.6 + sprintRacePos * 0.4;

  const gpQualiPos = clamp(
    Math.round(sampleNormal(sprintAdjustedQuali, params.qualiStdDev * 0.85)),
    1,
    20,
  );
  const qualiPoints = getQualifyingPoints(gpQualiPos);

  // Race DNF
  if (sampleBernoulli(params.dnfProb)) {
    const partialOvertakes = samplePoisson(params.expectedOvertakes * 0.3);
    return sprintTotal + qualiPoints + rules.raceDnfPenalty + partialOvertakes;
  }

  const raceDelta = sampleNormal(
    params.avgQualiToRaceDelta,
    params.raceDeltaStdDev,
  );
  const racePos = clamp(Math.round(gpQualiPos - raceDelta), 1, 20);
  const racePoints = getRacePoints(racePos);
  const posGained = gpQualiPos - racePos;
  const overtakes = samplePoisson(params.expectedOvertakes);
  const hasFastestLap = sampleBernoulli(params.fastestLapProb);
  const hasDotd = sampleBernoulli(params.dotdProb);

  const raceTotal =
    qualiPoints +
    racePoints +
    posGained +
    overtakes +
    (hasFastestLap ? rules.fastestLapRace : 0) +
    (hasDotd ? rules.driverOfTheDay : 0);

  return sprintTotal + raceTotal;
}

// ============================================================================
// Monte Carlo Runner — Driver
// ============================================================================

export function runDriverMonteCarlo(
  params: DriverSimParams,
  isSprintWeekend: boolean,
  iterations = 10_000,
  season = DEFAULT_SEASON,
): SimulationResult {
  const results = new Float64Array(iterations);

  for (let i = 0; i < iterations; i++) {
    results[i] = isSprintWeekend
      ? simulateSprintWeekend(params, season)
      : simulateRegularWeekend(params, season);
  }

  results.sort();

  const expectedPoints = mean(results);
  const components = computeComponentBreakdown(params, isSprintWeekend, season);

  return {
    expectedPoints,
    median: results[Math.floor(iterations / 2)],
    p10: results[Math.floor(iterations * 0.1)],
    p90: results[Math.floor(iterations * 0.9)],
    stdDev: standardDeviation(results),
    dnfProb: params.dnfProb,
    confidence: params.confidence,
    components,
  };
}

// ============================================================================
// Monte Carlo Runner — Constructor
// ============================================================================

export function runConstructorMonteCarlo(
  driver1Result: SimulationResult,
  driver2Result: SimulationResult,
  ctorParams: ConstructorSimParams,
  isSprintWeekend: boolean,
  iterations = 10_000,
  season = DEFAULT_SEASON,
): SimulationResult {
  const rules = getScoringRules(season);
  const results = new Float64Array(iterations);

  for (let i = 0; i < iterations; i++) {
    // For constructors, we sample from the combined distribution
    // using the pre-computed driver expected points as base
    const driver1Pts = sampleNormal(
      driver1Result.expectedPoints,
      driver1Result.stdDev,
    );
    const driver2Pts = sampleNormal(
      driver2Result.expectedPoints,
      driver2Result.stdDev,
    );

    const pitStopPts = Math.max(
      0,
      sampleNormal(ctorParams.expectedPitStopPoints, ctorParams.pitStopStdDev),
    );

    const flProb = isSprintWeekend
      ? ctorParams.sprintFastestLapProb
      : ctorParams.fastestLapProb;
    const hasFL = sampleBernoulli(flProb);

    results[i] =
      driver1Pts + driver2Pts + pitStopPts + (hasFL ? rules.fastestLapRace : 0);
  }

  results.sort();

  return {
    expectedPoints: mean(results),
    median: results[Math.floor(iterations / 2)],
    p10: results[Math.floor(iterations * 0.1)],
    p90: results[Math.floor(iterations * 0.9)],
    stdDev: standardDeviation(results),
    dnfProb: 0,
    confidence: ctorParams.confidence,
    components: {
      expectedQualiPoints: 0,
      expectedRacePoints: 0,
      expectedOvertakes: 0,
      expectedPosGained: 0,
      dnfImpact: 0,
      fastestLapEV: rules.fastestLapRace * ctorParams.fastestLapProb,
      dotdEV: 0,
      sprintPositionEV: 0,
      sprintOvertakesEV: 0,
      sprintFastestLapEV: isSprintWeekend
        ? rules.fastestLapSprint * ctorParams.sprintFastestLapProb
        : 0,
    },
  };
}

// ============================================================================
// Component Breakdown (deterministic expected values)
// ============================================================================

function computeComponentBreakdown(
  params: DriverSimParams,
  isSprintWeekend: boolean,
  season = DEFAULT_SEASON,
): ComponentBreakdown {
  const rules = getScoringRules(season);

  const expectedQualiPoints = getQualifyingPoints(
    Math.round(params.expectedQuali),
  );
  const expectedRacePos = clamp(
    Math.round(params.expectedQuali - params.avgQualiToRaceDelta),
    1,
    20,
  );
  const expectedRacePoints = getRacePoints(expectedRacePos);
  const expectedPosGained = params.avgQualiToRaceDelta;

  let sprintPositionEV = 0;
  let sprintOvertakesEV = 0;
  let sprintFastestLapEV = 0;

  if (isSprintWeekend) {
    const expectedSprintPos = clamp(
      Math.round(params.expectedQuali - params.avgSprintQualiToRaceDelta),
      1,
      20,
    );
    sprintPositionEV = getSprintPoints(expectedSprintPos);
    sprintOvertakesEV = params.expectedSprintOvertakes;
    sprintFastestLapEV = rules.fastestLapSprint * params.sprintFastestLapProb;
  }

  return {
    expectedQualiPoints,
    expectedRacePoints,
    expectedOvertakes: params.expectedOvertakes,
    expectedPosGained,
    dnfImpact: rules.raceDnfPenalty * params.dnfProb,
    fastestLapEV: rules.fastestLapRace * params.fastestLapProb,
    dotdEV: rules.driverOfTheDay * params.dotdProb,
    sprintPositionEV,
    sprintOvertakesEV,
    sprintFastestLapEV,
  };
}

// ============================================================================
// Statistical Helpers
// ============================================================================

function mean(arr: Float64Array): number {
  let sum = 0;
  for (let i = 0; i < arr.length; i++) {
    sum += arr[i];
  }
  return sum / arr.length;
}

function standardDeviation(arr: Float64Array): number {
  const m = mean(arr);
  let sumSq = 0;
  for (let i = 0; i < arr.length; i++) {
    const diff = arr[i] - m;
    sumSq += diff * diff;
  }
  return Math.sqrt(sumSq / arr.length);
}
